/**
 * Language Utilities
 * Universal language management for frontend and backend integration
 */

// Supported languages
export const SUPPORTED_LANGUAGES = ['fr', 'en'];
export const DEFAULT_LANGUAGE = 'fr';

/**
 * Extract 2-letter language code from full locale (e.g., 'fr-FR' -> 'fr')
 * @param {string} locale - Full locale string (e.g., 'fr-FR', 'en-US', 'fr')
 * @returns {string} - 2-letter language code
 */
export const extractLanguageCode = (locale) => {
  if (!locale) return DEFAULT_LANGUAGE;
  
  // If already a 2-letter code, return it
  if (locale.length === 2 && /^[a-z]{2}$/i.test(locale)) {
    return locale.toLowerCase();
  }
  
  // Extract first 2 letters before hyphen or underscore
  const match = locale.match(/^([a-z]{2})[-_]/i);
  if (match) {
    return match[1].toLowerCase();
  }
  
  // If no match, try first 2 characters
  const code = locale.substring(0, 2).toLowerCase();
  return SUPPORTED_LANGUAGES.includes(code) ? code : DEFAULT_LANGUAGE;
};

/**
 * Get language from localStorage
 * @returns {string|null} - Language code or null
 */
export const getLanguageFromStorage = () => {
  try {
    return localStorage.getItem('i18nextLng');
  } catch (e) {
    console.warn('localStorage not available:', e);
    return null;
  }
};

/**
 * Get language from cookie (fallback for old browsers)
 * @returns {string|null} - Language code or null
 */
export const getLanguageFromCookie = () => {
  try {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'i18nextLng' || name === 'lang') {
        return decodeURIComponent(value);
      }
    }
  } catch (e) {
    console.warn('Cookie access failed:', e);
  }
  return null;
};

/**
 * Get language from browser
 * @returns {string} - Language code
 */
export const getLanguageFromBrowser = () => {
  try {
    // Modern browsers
    if (navigator.language) {
      return extractLanguageCode(navigator.language);
    }
    // IE
    if (navigator.userLanguage) {
      return extractLanguageCode(navigator.userLanguage);
    }
    // Fallback
    if (navigator.languages && navigator.languages.length > 0) {
      return extractLanguageCode(navigator.languages[0]);
    }
  } catch (e) {
    console.warn('Browser language detection failed:', e);
  }
  return DEFAULT_LANGUAGE;
};

/**
 * Detect language with priority: localStorage > cookie > browser > default
 * @returns {string} - Detected language code
 */
export const detectLanguage = () => {
  // 1. Check localStorage
  const storedLang = getLanguageFromStorage();
  if (storedLang) {
    const code = extractLanguageCode(storedLang);
    if (SUPPORTED_LANGUAGES.includes(code)) {
      return code;
    }
  }
  
  // 2. Check cookie (fallback for old browsers)
  const cookieLang = getLanguageFromCookie();
  if (cookieLang) {
    const code = extractLanguageCode(cookieLang);
    if (SUPPORTED_LANGUAGES.includes(code)) {
      return code;
    }
  }
  
  // 3. Skip browser detection - always default to French
  // This ensures French is the default language regardless of browser settings
  
  // 4. Fallback to default (French)
  return DEFAULT_LANGUAGE;
};

/**
 * Save language to storage (localStorage and cookie)
 * @param {string} lang - Language code
 */
export const saveLanguage = (lang) => {
  const languageCode = extractLanguageCode(lang);
  
  if (!SUPPORTED_LANGUAGES.includes(languageCode)) {
    console.warn(`Unsupported language: ${lang}, falling back to ${DEFAULT_LANGUAGE}`);
    return;
  }
  
  // Save to localStorage
  try {
    localStorage.setItem('i18nextLng', languageCode);
  } catch (e) {
    console.warn('Failed to save language to localStorage:', e);
  }
  
  // Save to cookie as fallback (expires in 1 year)
  try {
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    document.cookie = `i18nextLng=${languageCode}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
    // Also set a simple 'lang' cookie for compatibility
    document.cookie = `lang=${languageCode}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  } catch (e) {
    console.warn('Failed to save language to cookie:', e);
  }
};

/**
 * Update HTML lang attribute for accessibility and SEO
 * @param {string} lang - Language code
 */
export const updateHtmlLang = (lang) => {
  const languageCode = extractLanguageCode(lang);
  if (document.documentElement) {
    document.documentElement.lang = languageCode;
  }
};

/**
 * Get Accept-Language header value for API requests
 * @param {string} lang - Language code
 * @returns {string} - Accept-Language header value
 */
export const getAcceptLanguageHeader = (lang) => {
  const languageCode = extractLanguageCode(lang);
  
  switch (languageCode) {
    case 'fr':
      return 'fr-FR,fr;q=0.9,en;q=0.8';
    case 'en':
      return 'en-US,en;q=0.9,fr;q=0.8';
    default:
      return 'fr-FR,fr;q=0.9,en;q=0.8';
  }
};

/**
 * Get language query parameter for API requests
 * @param {string} lang - Language code
 * @returns {string} - Language query parameter (e.g., 'lang=fr')
 */
export const getLanguageQueryParam = (lang) => {
  const languageCode = extractLanguageCode(lang);
  return `lang=${languageCode}`;
};

